import React from 'react';

import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateControl from 'components/Form/DateControl';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Country from 'components/Form/Country';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import { foreignFinancialInterestOptions, foreignInterestCoOwnerOptions, foreignInterestOwnerOptions } from 'constants/enums/foreignActivityOptions';
import Branch from 'components/Form/Branch';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import PVQName from 'components/Form/PVQName';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Dropdown from 'components/Form/Dropdown';
import { ForeignActivitiesFutureItemModel } from 'forms/sections/foreign/activities/future/types';

interface Props extends Partial<ForeignActivitiesFutureItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class FutureBenefit extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignActivitiesFutureItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      PrimaryPersonType: this.props.PrimaryPersonType,
      InterestType: this.props.InterestType,
      Country: this.props.Country,
      ExpectedDate: this.props.ExpectedDate,
      Reason: this.props.Reason,
      OtherExplanation: this.props.OtherExplanation,
      CountryFocused: this.props.CountryFocused,

      ...queue,
    });
  };

  updateName = (values: ForeignActivitiesFutureItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updatePrimaryPersonType = (values: ForeignActivitiesFutureItemModel['PrimaryPersonType']) => {
    this.update({
      PrimaryPersonType: values,
    });
  };

  updatInterestType = (values: ForeignActivitiesFutureItemModel['InterestType']) => {
    this.update({
      InterestType: values,
    });
  };

  updateCountry = (values: ForeignActivitiesFutureItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateReason = (values: ForeignActivitiesFutureItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateExpectedDate = (values: ForeignActivitiesFutureItemModel['ExpectedDate']) => {
    this.update({
      ExpectedDate: values,
    });
  };

  updateOtherExplanation = (values: ForeignActivitiesFutureItemModel['OtherExplanation']) => {
    this.update({
      OtherExplanation: values,
    });
  };

  updateInterestType = (values: ForeignActivitiesFutureItemModel['InterestType']) => {
    this.update({
      InterestType: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isOther = (value: string) => value === 'Other';

    const includesOther = (value: string) => typeof value === 'string' && value.includes('Other');
    const prefix = `foreign.pvq.activities.futureFinancialInterests`;

    return (
      <div className="future-benefit">
        <Field
          title={i18n.t(`${prefix}.label.expectedOwner`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PrimaryPersonType`}
        >

          <Dropdown
            attribute={`${attribute}.PrimaryPersonType`}
            name="PrimaryPersonType"
            onError={this.props.onError}
            onUpdate={this.updatePrimaryPersonType}
            value={this.props.PrimaryPersonType?.value}
            required={this.props.required}
          >
            {foreignInterestCoOwnerOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>

        </Field>

        {this.props.PrimaryPersonType?.value === 'Dependent children' && (
        <Field
          title={i18n.t(`${prefix}.label.dependentChildName`)}
          optional
          attribute={`${attribute}.Name`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            aria-label={i18n.t('foreign.pvq.activities.indirect.collection.who')}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="child-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>
        )}

        <Field
          title={i18n.t(`${prefix}.label.financialInterestType`)}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestType`}
        >

          <Dropdown
            attribute={`${attribute}.InterestType`}
            name="InterestType"
            onError={this.props.onError}
            onUpdate={this.updateInterestType}
            value={this.props.InterestType?.value}
            required={this.props.required}
          >
            {foreignFinancialInterestOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
          {includesOther(this.props.InterestType?.value) && (
          <Field
            attribute={`${attribute}.OtherExplanation`}
            title={i18n.t(`${prefix}.label.explanationOther`)}
          >
            <Textarea
              name="OtherExplanation"
              className="other-explanation"
              {...this.props.OtherExplanation}
              onUpdate={this.updateOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.OtherExplanation`}
            />
          </Field>
          )}

        </Field>

        <Field
          title={i18n.t(`${prefix}.label.countryAssociation`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t(`${prefix}.label.expectedReceiptDate`)}
          attribute={`${attribute}.ExpectedDate`}

          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="ExpectedDate"
            attribute={`${attribute}.ExpectedDate`}
            {...this.props.ExpectedDate}
            minDateEqualTo
            hideDay
            onUpdate={this.updateExpectedDate}
            onError={this.props.onError}
            className="ExpectedDate"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`${prefix}.label.expectedReason`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            className="Reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

      </div>

    );
  }
}
FutureBenefit.defaultProps = {

  onUpdate: () => { },
  CountryFocused: false,
  onError: (value, arr: ErrorCode) => arr,

  Name: {},
  PrimaryPersonType: {},
  InterestType: {},
  Country: {},
  ExpectedDate: {},
  Reason: {},
  OtherExplanation: {},

};
